import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  Message as MessageIcon,
  FiberNew as NewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { portalApi, PortalDashboard } from '../../services/portalService';

const PortalDashboardPage: React.FC = () => {
  const [dashboard, setDashboard] = useState<PortalDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await portalApi.getDashboard();
      setDashboard(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Fehler beim Laden des Dashboards');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!dashboard) {
    return null;
  }

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'offen':
        return 'warning';
      case 'in bearbeitung':
        return 'info';
      case 'erledigt':
        return 'success';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Willkommen im Kundenportal
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Dokumente
                  </Typography>
                  <Typography variant="h4">{dashboard.anzahlDokumente}</Typography>
                </Box>
                <DescriptionIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
              {dashboard.anzahlNeuesDokumente > 0 && (
                <Chip
                  size="small"
                  color="success"
                  label={`${dashboard.anzahlNeuesDokumente} neu`}
                  sx={{ mt: 1 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Offene Aufträge
                  </Typography>
                  <Typography variant="h4">{dashboard.anzahlOffeneTickets}</Typography>
                </Box>
                <AssignmentIcon sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Ungelesene Nachrichten
                  </Typography>
                  <Typography variant="h4">{dashboard.anzahlUngeleseneNachrichten}</Typography>
                </Box>
                <MessageIcon sx={{ fontSize: 40, color: 'info.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Items */}
      <Grid container spacing={3}>
        {/* Letzte Dokumente */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Aktuelle Dokumente</Typography>
                <Button size="small" onClick={() => navigate('/portal/dokumente')}>
                  Alle anzeigen
                </Button>
              </Box>
              {dashboard.letzteDokumente.length === 0 ? (
                <Typography color="textSecondary">Keine Dokumente vorhanden</Typography>
              ) : (
                <List dense>
                  {dashboard.letzteDokumente.map((dok) => (
                    <ListItemButton
                      key={dok.portalDokumentID}
                      onClick={() => navigate(`/portal/dokumente/${dok.portalDokumentID}`)}
                    >
                      <ListItemIcon>
                        <DescriptionIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={dok.titel}
                        secondary={dok.kategorie}
                      />
                      {dok.status === 'Neu' && (
                        <NewIcon color="success" fontSize="small" />
                      )}
                    </ListItemButton>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Letzte Tickets */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Meine Aufträge</Typography>
                <Button size="small" onClick={() => navigate('/portal/auftraege')}>
                  Alle anzeigen
                </Button>
              </Box>
              {dashboard.letzteTicketUpdates.length === 0 ? (
                <Typography color="textSecondary">Keine Aufträge vorhanden</Typography>
              ) : (
                <List dense>
                  {dashboard.letzteTicketUpdates.map((ticket) => (
                    <ListItemButton
                      key={ticket.ticketID}
                      onClick={() => navigate(`/portal/auftraege/${ticket.ticketID}`)}
                    >
                      <ListItemIcon>
                        <AssignmentIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={ticket.titel}
                        secondary={
                          <Chip
                            size="small"
                            label={ticket.status}
                            color={getStatusColor(ticket.status) as any}
                          />
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Letzte Nachrichten */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Nachrichten</Typography>
                <Button size="small" onClick={() => navigate('/portal/nachrichten')}>
                  Alle anzeigen
                </Button>
              </Box>
              {dashboard.letzteNachrichten.length === 0 ? (
                <Typography color="textSecondary">Keine Nachrichten vorhanden</Typography>
              ) : (
                <List dense>
                  {dashboard.letzteNachrichten.map((msg) => (
                    <ListItemButton
                      key={msg.portalNachrichtID}
                      onClick={() => navigate('/portal/nachrichten')}
                    >
                      <ListItemIcon>
                        <MessageIcon color={msg.gelesen ? 'disabled' : 'primary'} />
                      </ListItemIcon>
                      <ListItemText
                        primary={msg.betreff}
                        secondary={new Date(msg.gesendetAm).toLocaleDateString('de-CH')}
                        primaryTypographyProps={{
                          fontWeight: msg.gelesen ? 'normal' : 'bold',
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PortalDashboardPage;
